import { useApolloClient } from '@apollo/client';
import { Popover } from '@mui/material';
import { RiArrowDownSLine, RiCloseLine } from '@remixicon/react';
import Money from '@shared/utils/Money';
import { ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { asUTCDate } from 'shared/utils/DateTime';

import { AgButton, AgText } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';
import useAnchorEl from 'src/hooks/useAnchorEl';
import type { GridFilterOption } from 'src/modules/grids/DataGrid';

import BooleanFilter from './BooleanFilter';
import DateFilter from './DateFilter';
import type { FilterItemProps } from './filterItemTypes';
import NumberFilter from './NumberFilter';
import ReadOnlyFilter from './ReadOnlyFilter';
import { findRangeBound, toTypedBound } from './rangeBounds';
import ScalarTextFilter from './ScalarTextFilter';
import SelectFilter from './SelectFilter';
import TextFilter from './TextFilter';

export type { FilterItemProps };

/** What the chip shows where a value would go, on every filter type. */
const NO_FILTER_VALUE = '--';

/** A stored date bound as the chip shows it — its UTC calendar day. */
const chipDate = (value: unknown): string | undefined =>
  typeof value === 'string' || typeof value === 'number' ? asUTCDate(value)?.toFormat('MM/dd/yyyy') : undefined;

/** A column declares one shape or the other, so the first element settles the whole array. */
const isFilterOptionList = (options: string[] | GridFilterOption[]): options is GridFilterOption[] =>
  typeof options[0] === 'object';

export default function FilterItem(props: FilterItemProps) {
  const { ref, anchorEl, open, handleOpen, handleClose, handleForceClose } = useAnchorEl();

  // One id pair per mounted chip: the same field can be filtered on two grids
  // at once, and `aria-controls` pointing at a duplicate id is worse than none.
  const instanceId = useId();
  const popoverId = `grid-filter-popover-${instanceId}`;
  const popoverTitleId = `${popoverId}-title`;

  const { column, filter } = props;
  const colName = column.headerName ?? column.field;
  const colType = column.customFilterType ?? column.type;
  const active = filter.where?.length > 0;
  const client = useApolloClient();

  const getButtonValueDisplay = useCallback(async () => {
    // A chip is on screen before it has a value: `AddFilter` mounts it with an
    // empty `where`, and "Clear" inside the popover empties it again. Every
    // branch below indexes `where[0]`.
    if (!filter.where?.length) {
      return NO_FILTER_VALUE;
    }

    if (column.getFilterSelectOptions) {
      const categories = filter.where[0]?.in ?? filter.where[0]?.hasSome ?? [];
      if (categories.length === 1) {
        const options = column.getFilterSelectOptions;
        if (typeof options !== 'function' && isFilterOptionList(options)) {
          const selected = options.find((opt) => opt.value === categories[0]);
          if (selected) {
            return selected.label;
          }
        }
        return categories[0];
      }
      return <span className="badge badge-blue">{categories.length}</span>;
    }
    if (column.lookupFilterLabel) {
      const lookupValue = filter.where[0].equals ?? filter.where[0].has;
      if (lookupValue) {
        return await column.lookupFilterLabel(client, lookupValue);
      }
      return NO_FILTER_VALUE;
    }
    if (colType === 'date' || colType === 'dateTime') {
      const gte = chipDate(filter.where.find((cond) => cond.gte)?.gte);
      const lte = chipDate(filter.where.find((cond) => cond.lte)?.lte);
      if (gte && lte) {
        return `${gte} - ${lte}`;
      }
      if (gte) {
        return `After ${gte}`;
      }
      if (lte) {
        return `Before ${lte}`;
      }
      return NO_FILTER_VALUE;
    }
    if (colType === 'number' || colType === 'money') {
      // A money chip formats the stored cents; any other column shows the bound in the units the
      // user typed it in (the column's transform turns stored cents back into dollars).
      const shown = (stored: unknown) =>
        colType === 'money' && typeof stored === 'number' ? Money.toHuman(stored) : toTypedBound(column, stored);
      const gte = findRangeBound(filter.where, 'gte');
      const lte = findRangeBound(filter.where, 'lte');
      if (gte != null && lte != null) {
        return `${shown(gte)} - ${shown(lte)}`;
      }
      if (gte != null) {
        return `≥ ${shown(gte)}`;
      }
      if (lte != null) {
        return `≤ ${shown(lte)}`;
      }
      return NO_FILTER_VALUE;
    }
    if (colType === 'boolean') {
      return filter.where[0].equals ? 'Yes' : 'No';
    }
    return filter.where[0].contains ?? filter.where[0].equals ?? filter.where[0].has ?? NO_FILTER_VALUE;
  }, [column, filter.where, client, colType]);

  const [buttonValueDisplay, setButtonValueDisplay] = useState<ReactNode>('');

  useEffect(() => {
    getButtonValueDisplay().then((value) => {
      setButtonValueDisplay(value);
    });
  }, [getButtonValueDisplay]);

  return (
    <>
      <div className="group/filter inline-flex">
        <AgButton
          ref={ref}
          onPress={handleOpen}
          variant="white"
          shape="pill"
          className="text-14 gap-1"
          data-testid={props['data-testid']}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
        >
          <span className="text-brand-6">
            {colName}
            {active ? ':' : ''}
          </span>
          {active && <span className="font-bold">{buttonValueDisplay}</span>}
          <RiArrowDownSLine size={18} />
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full hover:bg-neutral-4 -mr-0.5',
              active && !open && 'hidden group-hover/filter:inline-flex'
            )}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              props.removeFilter();
            }}
          >
            <RiCloseLine size={16} />
          </span>
        </AgButton>
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            className: 'rounded-md mt-2 border border-neutral-5 shadow-md',
            sx: { overflow: 'hidden' },
            // The pop-out is a form, not a menu, so `role="dialog"` is the accurate answer — a native `<dialog>` is top-layer and modal-by-default, which would move it out of its anchored position.
            id: popoverId,
            role: 'dialog',
            'aria-labelledby': popoverTitleId,
          },
        }}
      >
        <div className="min-w-[200px] max-h-[min(40vh,500px)] flex flex-col overflow-hidden">
          <div className="px-3 pt-3 pb-2 border-b border-neutral-5">
            <AgText size="12" className="tracking-wider" id={popoverTitleId}>
              Filter by {colName}
            </AgText>
          </div>
          {/* Apply is a deliberate close: it must not fall inside `useAnchorEl`'s post-open click-away lock. */}
          {props.column.getFilterSelectOptions ? (
            <SelectFilter {...props} onClose={handleForceClose} />
          ) : colType === 'scalar_string' ? (
            <ScalarTextFilter {...props} onClose={handleForceClose} />
          ) : colType === 'date' || colType === 'dateTime' ? (
            <DateFilter {...props} onClose={handleForceClose} />
          ) : colType === 'number' || colType === 'money' ? (
            <NumberFilter {...props} onClose={handleForceClose} />
          ) : colType === 'boolean' ? (
            <BooleanFilter {...props} onClose={handleForceClose} />
          ) : colType === 'read_only' ? (
            <ReadOnlyFilter {...props} onClose={handleForceClose} />
          ) : (
            <TextFilter {...props} onClose={handleForceClose} />
          )}
        </div>
      </Popover>
    </>
  );
}
